import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { MERCHANT_ACCOUNTING_ROUTE } from "../../../../../../constants/routes"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { AccountingZReportsQuery, fetchZReports, FetchZReportsResponse } from "../zReportsApi"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { ZReportFlat } from "../zReportsTypes"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { DateTime } from "luxon"
import { useExportZReports } from "../hooks/useExportZReports"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const transformData = (data: FetchZReportsResponse): ZReportFlat[] => {
    return data.zReports.flatMap((report) => {
        return report.channels.map((channel) => {
            return {
                date: report.date,
                ...channel,
            }
        })
    })
}

export function ZReports() {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const { startDate, endDate } = useInitialDateFilters()

    const [dateState, setDateState] = useState<{ startDate: DateTime; endDate: DateTime }>({
        startDate: startDate,
        endDate: endDate,
    })

    const zReports = useApiQuery(fetchZReports, {
        queryName: AccountingZReportsQuery.Z_REPORTS_LIST,
        dependencies: {
            merchantId,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            dateType,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(zReports)

    const columns = useMemo<MRT_ColumnDef<ZReportFlat>[]>(
        () => [
            columnFactory.dateColumn(
                {
                    header: l10n.getString("merchant-accounting-z-reports-table-date-column"),
                    accessorKey: "date",
                },
                (row) => row.date,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-accounting-z-reports-table-pos-terminal-column"),
                accessorKey: "channel.displayText",
                enableGrouping: false,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("merchant-accounting-z-reports-table-amount-column"),
                    enableGrouping: false,
                },
                (row) => row.sales,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    const exportReports = useExportZReports({
        data: zReports.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    return (
        <>
            <LocalizedStrict id="merchant-accounting-z-reports-page-header" attrs={{ title: true }}>
                <PageHeader page={Page.ACCOUNTING_Z_REPORTS} title="Z-reports" icon={<FontAwesomeIcon icon={faShop} />}>
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ACCOUNTING_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-accounting-z-report">
                            <Breadcrumb>Z-reports</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{
                    density: "spacious",
                    expanded: true,
                    grouping: ["date"],
                    columnVisibility: {
                        "mrt-row-expand": false,
                    },
                }}
                enableRowActions={false}
                groupedColumnMode={false}
                enableExpanding={false}
                positionToolbarAlertBanner="none"
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton onClick={() => exportReports()} />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                        <LocalizedStrict
                            id="merchant-accounting-z-reports-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </LocalizedStrict>
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
